import { Link } from 'react-router';
import type { TargetDTO } from '@devrobot/shared';
import { ArrowRightLeft, CircleCheck, CircleHelp, CircleOff, Database, HardDrive } from 'lucide-react';
import { IconChip } from '../../components/PageHeader';

const CREDENTIAL: Record<TargetDTO['databases'][number]['credential'], string> = {
  generated: 'generated by DevRobot',
  supplied: 'supplied in configuration',
  adopted: 'adopted from existing data',
  none: 'none',
};

type Volume = TargetDTO['volumes'][number];
type DatabaseInfo = TargetDTO['databases'][number];

const HEAD = 'text-xs tracking-wide text-base-content/50 uppercase';

function VolumeState({ v }: { v: Volume }) {
  if (v.exists === true)
    return (
      <span className="badge badge-soft badge-success badge-sm gap-1">
        <CircleCheck className="size-3" aria-hidden="true" /> exists
      </span>
    );
  if (v.exists === false)
    return (
      <span className="badge badge-soft badge-neutral badge-sm gap-1">
        <CircleOff className="size-3" aria-hidden="true" /> not found
      </span>
    );
  return (
    <span className="badge badge-ghost badge-sm gap-1" title="The agent has not reported this volume">
      <CircleHelp className="size-3" aria-hidden="true" /> unknown
    </span>
  );
}

function BackupSupport({ d }: { d: DatabaseInfo }) {
  return d.backupSupported ? (
    <span className="badge badge-soft badge-success badge-sm gap-1">
      <CircleCheck className="size-3" aria-hidden="true" /> supported
    </span>
  ) : (
    <span className="badge badge-ghost badge-sm gap-1">
      <CircleOff className="size-3" aria-hidden="true" /> no adapter
    </span>
  );
}

export function DataTab({ target: t }: { target: TargetDTO }) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-sm text-base-content/65">Removing the application keeps these volumes. Deleting data is a separate action that requires typed confirmation and the Owner role.</p>
        {t.can.backup && !t.removedAt && t.state.process !== 'not-deployed' ? (
          <Link to={`/migrations/new?target=${encodeURIComponent(t.id)}`} className="link inline-flex shrink-0 items-center gap-1.5 text-sm">
            <ArrowRightLeft className="size-3.5" aria-hidden="true" /> Migrate this data to another VPS or environment
          </Link>
        ) : null}
      </div>
      <div>
        <h3 className="mb-2 flex items-center gap-2 font-semibold">
          <IconChip icon={HardDrive} tone="secondary" size="sm" /> Volumes
        </h3>
        {t.volumes.length === 0 ? (
          <p className="text-sm text-base-content/60">No persistent volumes are declared.</p>
        ) : (
          <div className="card dr-surface overflow-hidden">
            <ul className="divide-y divide-hairline md:hidden">
              {t.volumes.map((v) => (
                <li key={v.name} className="space-y-1 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-sm font-medium break-anywhere">{v.name}</span>
                    <VolumeState v={v} />
                  </div>
                  <p className="text-xs text-base-content/60">
                    {v.declared ? 'in manifest' : 'found on server only'}
                    {v.description ? ` · ${v.description}` : ''}
                  </p>
                </li>
              ))}
            </ul>
            <div className="overflow-x-auto max-md:hidden">
              <table className="table table-sm">
                <thead>
                  <tr className={HEAD}>
                    <th>Volume</th>
                    <th>On the server</th>
                    <th>Declared</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {t.volumes.map((v) => (
                    <tr key={v.name} className="hover:bg-base-200/50">
                      <td className="font-mono text-sm">{v.name}</td>
                      <td>
                        <VolumeState v={v} />
                      </td>
                      <td className="text-sm">{v.declared ? 'in manifest' : 'found on server only'}</td>
                      <td className="text-sm text-base-content/65">{v.description ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <div>
        <h3 className="mb-2 flex items-center gap-2 font-semibold">
          <IconChip icon={Database} tone="info" size="sm" /> Databases
        </h3>
        {t.databases.length === 0 ? (
          <p className="text-sm text-base-content/60">No databases are declared.</p>
        ) : (
          <div className="card dr-surface overflow-hidden">
            <ul className="divide-y divide-hairline md:hidden">
              {t.databases.map((d) => (
                <li key={d.service} className="space-y-1 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm">
                      <span className="font-mono font-medium">{d.service}</span> <span className="text-base-content/60">({d.type})</span>
                    </span>
                    <BackupSupport d={d} />
                  </div>
                  <p className="text-xs text-base-content/60">
                    volume <span className="font-mono">{d.volume}</span> · credential {CREDENTIAL[d.credential]}
                  </p>
                </li>
              ))}
            </ul>
            <div className="overflow-x-auto max-md:hidden">
              <table className="table table-sm">
                <thead>
                  <tr className={HEAD}>
                    <th>Service</th>
                    <th>Type</th>
                    <th>Volume</th>
                    <th>Credential</th>
                    <th>Backups</th>
                  </tr>
                </thead>
                <tbody>
                  {t.databases.map((d) => (
                    <tr key={d.service} className="hover:bg-base-200/50">
                      <td className="font-mono text-sm">{d.service}</td>
                      <td className="text-sm">{d.type}</td>
                      <td className="font-mono text-sm">{d.volume}</td>
                      <td className="text-sm">{CREDENTIAL[d.credential]}</td>
                      <td>
                        <BackupSupport d={d} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
